import { useActivityLogs } from "@/hooks/queries/dashboard/useActivityLogs";
import { Activity, Clock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

const translateEvent = (event: string) => {
  switch (event) {
    case "created":
      return "criou";
    case "updated":
      return "atualizou";
    case "deleted":
      return "excluiu";
    default:
      return event;
  }
};

const formatEntityName = (subjectType: string) => {
  if (!subjectType) return "Registro";
  const parts = subjectType.split("\\");
  const model = parts[parts.length - 1];

  switch (model) {
    case "Patient":
      return "Paciente";
    case "Scheduling":
      return "Agendamento";
    case "FinanceEntry":
      return "Lançamento";
    case "User":
      return "Usuário";
    default:
      return model;
  }
};

export function DashboardActivityFeed() {
  const { logs, isLoading } = useActivityLogs(5);

  return (
    <section className="flex h-full flex-col rounded-xl border border-app-border bg-app-surface p-6 shadow-premium-sm lg:col-span-1">
      <header className="mb-4">
        <h3 className="text-lg font-semibold tracking-tight text-app-fg-strong">
          Atividade recente
        </h3>
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto pr-2">
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-3">
                <div className="size-8 shrink-0 rounded-full bg-app-surface-muted" />
                <div className="flex-1 space-y-2 pt-1">
                  <div className="h-4 w-3/4 rounded bg-app-surface-muted" />
                  <div className="h-3 w-1/2 rounded bg-app-surface-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center space-y-2 text-center">
            <Activity
              className="size-8 text-app-fg-subtle"
              strokeWidth={1.5}
            />
            <p className="text-sm text-app-fg-muted">
              Nenhuma atividade recente.
            </p>
          </div>
        ) : (
          <ol className="relative ml-4 space-y-5 border-l border-app-border-subtle pb-2">
            {logs.map((log) => (
              <li key={log.id} className="relative pl-6">
                <span className="absolute -left-[5px] top-1 size-2.5 rounded-full border-2 border-brand bg-app-surface ring-4 ring-app-surface" />
                <div className="flex flex-col">
                  <p className="text-sm leading-tight text-app-fg-strong">
                    <span className="font-semibold">
                      {log.causer?.name || "Sistema"}
                    </span>{" "}
                    <span className="text-app-fg-muted">
                      {translateEvent(log.event)}
                    </span>{" "}
                    <span className="font-medium text-brand">
                      {formatEntityName(log.subject_type)}
                    </span>
                  </p>
                  <p className="mt-1 flex items-center gap-1 text-xs text-app-fg-muted">
                    <Clock className="size-3" strokeWidth={2} />
                    {formatDistanceToNow(new Date(log.created_at), {
                      addSuffix: true,
                      locale: ptBR,
                    })}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}
